import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { XCircle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { da } from "date-fns/locale";

interface CancelBookingDialogProps {
  booking: {
    id: string;
    start_at: string;
    end_at: string;
    total_amount: number;
    status: string;
    stripe_payment_intent_id: string | null;
    listings?: {
      title: string;
      cancellation_policy?: string;
    } | null;
  };
  onCancelComplete?: () => void;
}

export default function CancelBookingDialog({
  booking,
  onCancelComplete,
}: CancelBookingDialogProps) {
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);

  const canCancel = booking.status === "pending" || booking.status === "confirmed";

  // Calculate refund amount when dialog opens
  const calculateRefund = () => {
    try {
      const now = new Date();
      const startDate = new Date(booking.start_at);
      const hoursUntilStart = (startDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      const daysUntilStart = hoursUntilStart / 24;

      const cancellationPolicy = booking.listings?.cancellation_policy || 'mod';
      let refundPercentage = 0;
      
      if (cancellationPolicy === 'flex') {
        if (hoursUntilStart > 24) refundPercentage = 100;
        else if (hoursUntilStart >= 12) refundPercentage = 50;
        else refundPercentage = 0;
      } else if (cancellationPolicy === 'mod') {
        if (daysUntilStart > 5) refundPercentage = 100;
        else if (daysUntilStart >= 2) refundPercentage = 50;
        else refundPercentage = 0;
      } else if (cancellationPolicy === 'strict') {
        if (daysUntilStart > 7) refundPercentage = 50;
        else refundPercentage = 0;
      }

      const calculatedRefund = Math.round((booking.total_amount * refundPercentage) / 100);
      const PROCESSING_FEE = 500; // 5 kr
      const finalRefund = calculatedRefund > 0 ? Math.max(0, calculatedRefund - PROCESSING_FEE) : 0;
      
      setRefundAmount(finalRefund);
    } catch (error) {
      console.error("Error calculating refund:", error);
      toast.error("Kunne ikke beregne refundering");
    }
  };

  const handleCancel = async () => {
    if (!canCancel) return;

    setCancelling(true);
    try {
      // Kald edge function til at håndtere refund
      const { data, error } = await supabase.functions.invoke("cancel-booking", {
        body: {
          bookingId: booking.id,
          paymentIntentId: booking.stripe_payment_intent_id,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(data?.message || "Booking annulleret");
        setOpen(false);
        onCancelComplete?.();
      } else {
        throw new Error(data?.error || "Kunne ikke annullere booking");
      }
    } catch (error: any) {
      console.error("Cancel error:", error);
      toast.error(error.message || "Kunne ikke annullere booking");
    } finally {
      setCancelling(false);
    }
  };

  if (!canCancel) {
    return null;
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          calculateRefund();
          setOpen(true);
        }}
      >
        <XCircle className="h-4 w-4 mr-2" />
        Annuller
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuller booking?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Er du sikker på at du vil annullere denne booking? Betalingen vil
                  automatisk blive refunderet.
                </p>

                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="font-medium">
                    {booking.listings?.title || "Parkeringsplads"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(booking.start_at), "d. MMM yyyy", { locale: da })}
                    {" - "}
                    {format(new Date(booking.end_at), "d. MMM yyyy", { locale: da })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      Betalt: {Math.round(booking.total_amount / 100)} kr
                    </Badge>
                    <Badge>{booking.status}</Badge>
                  </div>
                  {refundAmount !== null && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Du får refunderet:</span>
                        <span className="text-lg font-bold text-primary">
                          {Math.round(refundAmount / 100)} kr
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        (inkl. 5 kr administrationsgebyr)
                      </p>
                    </div>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  Denne handling kan ikke fortrydes.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={cancelling}>
              Fortryd
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {cancelling ? "Annullerer..." : "Ja, annuller booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
